import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import {
  IAbstractProduct,
  IAbstractProductIncluded,
  ICachedProductSkuAndRequiredReload,
  IProductLabel,
} from '../../models/abstract-product.models';
import { IContractParams } from '../../models/catalog.models';
import { IImage, IPrice, ISystemDetails } from '../../models/common.models';
import { MarketingFacade } from '../../facades/marketing.facade';
import { CatalogFacade } from '../../facades/catalog.facade';
import { SeoFacade } from '../../facades/seo.facade';
import { ESeoData, ISeoPageData } from '../../configurations/seo';
import { AnalyticsService } from '../../analytics/analytics.service';
import { PageTypes } from '../../analytics/enums/pageTypes';
import { environment } from '../../../environments/environment';
import { WishlistFacade } from '../../facades/wishlist.facade';
import { AuthorizationFacade } from '../../facades/authorization.facade';
import { AppUtils } from '../../utils/app.utils';
import { EProductCategoryDetails, EProductDetails } from '../../configurations/product-details';
import { ProductUtils } from '../../utils/product.utils';
import {
  EFeatureToggles,
  EGlueResource,
  EInstalledBaseTabs,
  EStoreFeatures,
  EStoreTypes,
  EUserRoles,
} from '../../configurations/common';
import { IAddToCartAdditionalFields } from '../../models/cart.models';
import { ISimpleInstallBaseProduct } from '../../models/installedbase.models';
import { IconType } from '../../models/settings.model';
import { CustomerFacade } from '../../facades/customer.facade';
import { I18nService } from '../../services';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { PriceUtils } from '../../utils/price.utils';
import { CartUtils } from '../../utils/cart.utils';
import { ImageUtils } from '../../utils/image.utils';
import { ImagePreset } from '../../models/image.models';
import { IBaseWishlist } from '../../models/wishlist.models';

@Component({
  selector: 'app-product-page',
  templateUrl: './product-page.component.html',
  styleUrls: ['./product-page.component.scss'],
  standalone: false,
})
export class ProductPageComponent implements OnInit, OnDestroy {
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly catalogFacade: CatalogFacade = inject(CatalogFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly seoFacade: SeoFacade = inject(SeoFacade);
  private readonly wishlistFacade: WishlistFacade = inject(WishlistFacade);
  private readonly authorizationFacade: AuthorizationFacade = inject(AuthorizationFacade);
  private readonly i18nService: I18nService = inject(I18nService);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);

  iconType: typeof IconType = IconType;
  productInfoLoaded: boolean = false;
  isAddToCartInProgress$: Observable<any> = this.marketingFacade.isAddItemOperationInProgress();
  loadingCartDataInProgress: boolean = true;
  productQuantity: number = 1;
  largeUrls: string[] = [];
  smallUrls: string[] = [];
  sku!: string;
  defaultPrice!: IPrice;
  mainPicture: string = environment.defaultImg;
  secondPrice: IPrice | null = null;
  housingFee: string | null = null;
  listPriceWithoutReplacement: IPrice | null = null;
  listPriceWithReplacement: IPrice | null = null;
  yourPriceWithoutReplacement: IPrice | null = null;
  yourPriceWithReplacement: IPrice | null = null;
  installBaseProducts: ISimpleInstallBaseProduct[] = [];
  isQuantityValid: boolean = true;
  abstractProduct!: IAbstractProduct;
  isAbstractProductLoading$: Observable<boolean> = new Observable<boolean>();
  recentlyOpenedProducts: ICachedProductSkuAndRequiredReload[] = [];
  concreteProduct!: IAbstractProductIncluded;
  error: any;
  defaultImg: string = environment.defaultImg;
  addNewModalActive: boolean = false;
  addItemToCartModalActive: boolean = false;
  concreteSku: string | null = null;
  wishlists: IBaseWishlist[] = [];
  isWishlistsActive: boolean = this.configurationFacade.isFeatureAvailable(EStoreFeatures.WISHLISTS);
  isProductTagsActive: boolean = this.configurationFacade.isFeatureAvailable(EStoreFeatures.PRODUCT_TAGS);
  labels: IProductLabel[] = [];
  displayConfig: EProductDetails[] = [];
  productDetailsEnum: typeof EProductDetails = EProductDetails;
  materialNumber!: string;
  flNumber!: string;
  stepNumber!: number;
  showPriceDisclaimer!: boolean;
  showModalSelectEquipment: boolean = false;
  isUsStore: boolean = false;
  isCaStore: boolean = false;
  isSapStore: boolean = AppUtils.isSapStore();
  isSparePartsEnabled: boolean = false;
  isCpqEnabled: boolean = false;
  currentCartId!: string;
  hasCartContract: boolean = false;
  itemToAdd: any;
  isInStore: boolean = false;
  isSparePart: boolean = false;
  wasRedirectedFromEquipmentPage: boolean = false;
  hasContractParams: boolean = false;
  systemDetails?: ISystemDetails;
  contractParams: IContractParams = {
    'fl-number': '',
    'rel-product-sysivk': '',
  };
  companyRoles: EUserRoles[] = [];
  pricingGroupToggle: boolean = false;
  isMyInstalledBaseFlowEnabled: boolean = false;
  isNonEquipmentPurchaseEnabled: boolean = false;
  supportedCategoriesForNonEquipmentPurchase: string[] = [];
  isPredefinedEquipmentFlowEnabled: boolean = false;

  isDeliveryAvailabilityEnabled$: Observable<boolean> =
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.PDP_DELIVERY_AVAILABILITY);
  isEstimatedDeliveryEnabled$: Observable<boolean> =
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.PDP_ESTIMATED_DELIVERY);

  private readonly installBaseType: string = 'install-base';
  private readonly concreteProductType: string = 'concrete-products';
  private readonly unsubscribe$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.isAbstractProductLoading$ = this.catalogFacade.selectIsAbstractProductLoading();
    this.selectIsMyInstalledBaseFlowEnabled();
    this._selectIsPredefinedEquipmentFlowEnabled();
    this._isNonEquipmentPurchaseEnabled();
    this.selectUsFeatures();

    this.selectPricingGroupToggle();
    this.isUsStore = AppUtils.isStoreActive(EStoreTypes.US);
    this.isCaStore = AppUtils.isStoreActive(EStoreTypes.CA);
    this.isInStore = AppUtils.isStoreActive(EStoreTypes.IN);
    this.showPriceDisclaimer = AppUtils.getCurrentStore().showPriceDisclaimer;

    this.selectLoadingCartDataInProgress();
    this.selectCartAndSetParamsForFL();

    if (this.isUsStore) {
      this.selectHasCartContract();
    }

    this.getSkuFromRouteAndGetProductBySku();
    this.getCustomerCompanyRoles();
  }

  selectUsFeatures(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.CPQ).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.isCpqEnabled = value;
    });

    this.configurationFacade.isFeatureEnabled(EFeatureToggles.SPARE_PARTS).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.isSparePartsEnabled = value;
    });
  }

  /**
   * Method to select Pricing Group toggle
   * @private
   */
  private selectPricingGroupToggle(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.PRODUCT_PRICING_GROUP).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.pricingGroupToggle = value;
    });
  }

  getWishlistsFromStore(): void {
    this.wishlistFacade.selectUsersWishlists().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(
      wishlists => {
        this.wishlists = wishlists;
      },
    );
  }

  selectHasCartContract(): void {
    this.marketingFacade.selectHasCartContract().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
        this.hasCartContract = value;
      },
    );
  }

  getDisplayConfig(webshopProductCategory: string): void {
    const mappedWebshopProductCategory: string = EProductCategoryDetails[webshopProductCategory] ?? 'default';
    this.i18nService.getTranslationByKey(`accordion.${mappedWebshopProductCategory}`).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe((productCategoryValue: string) => {
      this.displayConfig = productCategoryValue.split(';')
        .map((value: string): string => value.trim())
        .filter((value: string): value is EProductDetails => this.isProductDetail(value));
    });
  }

  displayProductDetail(section: EProductDetails): boolean {
    return this.displayConfig.includes(section);
  }

  private isProductDetail(value: string): value is EProductDetails {
    return Object.values(EProductDetails).some((detail: EProductDetails): boolean => detail === value);
  }

  selectAbstractProductError(): void {
    this.catalogFacade.selectAbstractProductError()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(error => {
        if (!this.abstractProduct && error) {
          this.router.navigate(['page-not-found']).then(() => this.catalogFacade.clearAbstractProduct());
        }
      });
  }

  initAbstractProductPrices(): void {
    const abstractProductPrices: any = this.abstractProduct.included.find(
      (item: any): boolean => item.type === EGlueResource.ABSTRACT_PRODUCT_PRICES,
    );
    if (abstractProductPrices) {
      const defaultPrice: IPrice | null | undefined = PriceUtils.getDefaultPrice(abstractProductPrices.attributes.prices);
      this.defaultPrice = defaultPrice ?? {
        priceTypeName: '',
        grossAmount: 0,
        currency: {
          code: '',
          name: '',
          symbol: '',
        },
      };
      this.secondPrice = PriceUtils.getGuestPrice(abstractProductPrices.attributes.prices) ?? null;
    }

    // Extract "without replacement" prices from concrete-product-prices with _S suffix
    const withoutReplacementPrices = this.abstractProduct.included?.find(
      (item: any): boolean => item.type === EGlueResource.CONCRETE_PRODUCT_PRICES && item.id?.endsWith('_S'),
    );
    if (withoutReplacementPrices?.attributes?.prices) {
      this.listPriceWithoutReplacement = PriceUtils.getGuestPrice(withoutReplacementPrices.attributes.prices) ?? null;
      this.yourPriceWithoutReplacement = PriceUtils.getDefaultPrice(withoutReplacementPrices.attributes.prices) ?? null;
    }

    // Extract "with replacement" prices from concrete-product-prices with _W suffix
    const withReplacementPrices = this.abstractProduct.included?.find(
      (item: any): boolean => item.type === EGlueResource.CONCRETE_PRODUCT_PRICES && item.id?.endsWith('_W'),
    );
    if (withReplacementPrices?.attributes?.prices) {
      this.listPriceWithReplacement = PriceUtils.getGuestPrice(withReplacementPrices.attributes.prices) ?? null;
      this.yourPriceWithReplacement = PriceUtils.getDefaultPrice(withReplacementPrices.attributes.prices) ?? null;
    }

    // Extract "housing fee" from abstract-product
    this.housingFee = PriceUtils.getHousingFee(this.abstractProduct) ?? null;
  }

  initAbstractProductImages(): void {
    const abstractProductImages: any = this.abstractProduct.included.find(
      (item: any): boolean => item.type === EGlueResource.ABSTRACT_PRODUCT_IMAGE_SETS,
    );
    if (abstractProductImages?.attributes?.imageSets[0]) {
      const photosUrls: IImage[] = abstractProductImages.attributes.imageSets[0].images;
      this.largeUrls = photosUrls.map((photo: IImage): string => ImageUtils.addPreset(photo.externalUrlLarge, ImagePreset.Big));
      this.smallUrls = photosUrls.map((photo: IImage): string => ImageUtils.addPreset(photo.externalUrlSmall, ImagePreset.Big));
      this.mainPicture = this.largeUrls[0];
      this.seoFacade.handleProductSchema(this.abstractProduct, this.largeUrls);
    }
  }

  /**
   * If equipment is required and not selected, open modal to select equipment.
   * If cart has contract, open modal to confirm adding item to cart.
   * Otherwise, add item directly to cart.
   *
   * @param additionalFields
   */
  addProductToCart(additionalFields: IAddToCartAdditionalFields = {}): void {
    if (this.isQuantityValid && !this.loadingCartDataInProgress) {
      const systemDetails: ISystemDetails | undefined = this.systemDetails;

      const sku: string = additionalFields.sku || this.concreteSku || this.abstractProduct.sku;

      const data: {
        sku: string;
        name: string;
        systemDetails?: ISystemDetails;
        systemDetailsPerItem?: { itemId: string; systemDetails: ISystemDetails }[];
        sap_product_sparepart?: string;
      } = {
        sku,
        name: this.abstractProduct.name,
        sap_product_sparepart: (this.concreteProduct?.attributes?.attributes?.sap_product_sparepart
          ?? this.abstractProduct?.attributes?.sap_product_sparepart) as string | undefined,
      };

      if (this.hasCartContract) {
        this.itemToAdd = [{
          data: data,
          isConcrete: !!this.concreteSku,
          quantity: this.productQuantity,
        }];
        this.addItemToCartModalActive = true;
        return;
      }
      const webshopProductCategory: string = typeof this.abstractProduct.attributes?.mcm_webshop_product_category === 'string'
        ? this.abstractProduct.attributes.mcm_webshop_product_category
        : '';
      const isEquipmentRequired: boolean = CartUtils.determineIfEquipmentIsRequired(
        this.isMyInstalledBaseFlowEnabled,
        this.isNonEquipmentPurchaseEnabled,
        this.supportedCategoriesForNonEquipmentPurchase,
        webshopProductCategory,
      );

      if (!this.isPredefinedEquipmentFlowEnabled && isEquipmentRequired) {
        if (!systemDetails?.siemensEquipmentId) {
          this.showModalSelectEquipment = true;
          return;
        }
      }

      if (additionalFields.addSystemDetails && systemDetails) {
        data.systemDetails = systemDetails;
        data.systemDetailsPerItem = [{
          itemId: data.sku,
          systemDetails,
        }];
      }

      this.analyticsService.setProducts(this.abstractProduct);
      this.analyticsService.trackCart('cart.add');
      this.marketingFacade.addProductToCart(
        data,
        !!this.concreteSku,
        this.productQuantity,
      );
    }
    this.productQuantity = 1;
  }

  getItemConcreteSku(): string | null {
    if (this.abstractProduct.attributeMap?.product_concrete_ids?.length > 0) {
      return this.abstractProduct.attributeMap.product_concrete_ids[0];
    } else {
      return null;
    }
  }

  getDetailAttribute(attributeValue: string | null): string {
    return ProductUtils.getDetailAttribute(attributeValue);
  }

  selectEquipment(): void {
    let commands: string = '/my-installed-base';
    let tab: EInstalledBaseTabs | null = EInstalledBaseTabs.EQUIPMENT;

    if (this.companyRoles.includes(EUserRoles.BusinessPartnerApprover) ||
      this.companyRoles.includes(EUserRoles.BusinessPartnerBuyer)) {
      commands = '/equipment-selection';
      tab = null;
    }

    let queryParams: {tab: EInstalledBaseTabs | null; lastSku?: string; pricing_group?: string} = {
      tab: tab,
      lastSku: this.sku,
    };

    if (this.pricingGroupToggle) {
      const pricingGroup: string = typeof this.abstractProduct.attributes?.sap_p40_material_pricing_group_3 === 'string'
        ? this.abstractProduct.attributes.sap_p40_material_pricing_group_3
        : '';
      queryParams = {
        ...queryParams,
        pricing_group: pricingGroup,
      };
    }

    this.router.navigate([commands], {
      queryParams: queryParams,
    }).then();
  }

  setContractParams(params: Params): void {
    this.contractParams['fl-number'] = params['fl-number'] ?? '';
    this.contractParams['rel-product-sysivk'] = params['rel-product-sysivk'] ?? '';
    const relProductSysivk: string = this.contractParams['rel-product-sysivk'] ?? '';
    const flNumber: string = this.contractParams['fl-number'] ?? '';
    this.hasContractParams = relProductSysivk.length > 0 && flNumber.length > 0;
  }

  private selectCartAndSetParamsForFL(): void {
    combineLatest([
      this.marketingFacade.selectCart(),
      this.route.queryParams,
    ]).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(([cart, params]) => {
      if (cart) {
        this.currentCartId = cart.id;
        this.systemDetails = cart.attributes?.systemDetails;

        this.stepNumber = 1;
        this.materialNumber = this.systemDetails?.materialNumber ?? params['rel-product-sysivk'];
        this.flNumber = this.systemDetails?.siemensEquipmentId ?? params['fl-number'];
      }

      this.setContractParams(params);

      if (this.hasContractParams) {
        this.wasRedirectedFromEquipmentPage = true;
        setTimeout(() => this.wasRedirectedFromEquipmentPage = false, 5000);
      }
    });
  }

  private selectLoadingCartDataInProgress(): void {
    this.marketingFacade.selectLoadingCartDataInProgress().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(loadingCartDataInProgress => {
      this.loadingCartDataInProgress = loadingCartDataInProgress;
    });
  }

  getCustomerCompanyRoles(): void {
    this.customerFacade.getCustomerCompanyRoles()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((companyRoles: EUserRoles[]) => {
        this.companyRoles = companyRoles;
      });
  }

  /**
   * Get sku from actual route and get/select Abstract product by the given SKU
   */
  getSkuFromRouteAndGetProductBySku(): void {
    this.route.params.subscribe(params => {
      this.sku = params.sku;
      if (params.sku) {
        this.productQuantity = 1;
        if (!this.hasProductBeenOpened(params.sku)) {
          this.catalogFacade.loadAbstractProductData(params.sku);
        }
        this.selectRecentlyOpenedProductBySku(params.sku);
        this.selectAbstractProductError();

        this.authorizationFacade.selectIsUserLoggedIn().pipe(takeUntil(this.unsubscribe$)).subscribe(isLoggedIn => {
          if (isLoggedIn) {
            this.wishlistFacade.dispatchUsersWishlistRetrieve();
            this.getWishlistsFromStore();
          }
        });
      }
    });
  }

  selectRecentlyOpenedProductBySku(sku: string): void {
    this.catalogFacade.selectRecentlyOpenedProductBySku(sku)
      .pipe(
        takeUntil(this.unsubscribe$),
      )
      .subscribe(abstractProduct => {
        if (abstractProduct && abstractProduct.sku === this.sku) {
          this.productInfoLoaded = true;
          this.abstractProduct = abstractProduct;
          this.getDisplayConfig(this.abstractProduct.attributes?.['mcm_webshop_product_category'] as string);
          this.concreteProduct = abstractProduct.included?.find((item: any): boolean => item.type === this.concreteProductType);
          this.isSparePart = !this.concreteProduct?.attributes.productConfigurationInstance;
          this.concreteSku = this.getItemConcreteSku();
          this.analyticsService.setProducts(abstractProduct);
          this.analyticsService.trackPageReady('product detail', PageTypes.PRODUCT_PAGE, 'product-page');
          this.installBaseProducts = abstractProduct.included?.filter((item: any): boolean => item.type === this.installBaseType);
          this.labels = abstractProduct.included?.filter((item: any): boolean => item.type === 'product-labels');
          const seoPageData: ISeoPageData = {
            titleSource: this.route.snapshot.data[ESeoData.seoTitleSource],
            titles: [abstractProduct.metaTitle],
            description: abstractProduct.metaDescription,
            keywords: abstractProduct.metaKeywords,
            canonicalUrl: {
              rel: 'canonical',
              href: abstractProduct.canonicalUrl || this.router.url,
            },
          };
          this.seoFacade.setMetaFromComponent(seoPageData);
          this.initAbstractProductPrices();
          this.initAbstractProductImages();
        }
      });
  }

  /**
   * Method checks whether the product has been recently opened in the session
   *
   * @param {string} sku
   * @returns {boolean}
   */
  hasProductBeenOpened(sku: string): boolean {
    this.catalogFacade.selectRecentlyOpenedProductsSkus()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((productSkus: ICachedProductSkuAndRequiredReload[]) => {
        this.recentlyOpenedProducts = productSkus;
      });
    return this.recentlyOpenedProducts.some((product => product.sku === sku));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Method for retrieving IsMyInstalledBaseFlowEnabled feature toggle value
   * @private
   */
  private selectIsMyInstalledBaseFlowEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.MY_INSTALLED_BASE_FLOW).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isMyInstalledBaseFlowEnabled => {
      this.isMyInstalledBaseFlowEnabled = isMyInstalledBaseFlowEnabled;
    });
  }

  /**
   * Method for retrieving IsPredefinedEquipmentFlowEnabled feature toggle value
   * @private
   */
  private _selectIsPredefinedEquipmentFlowEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.PREDEFINED_EQUIPMENT_FLOW).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isPredefinedEquipmentFlowEnabled => {
      this.isPredefinedEquipmentFlowEnabled = isPredefinedEquipmentFlowEnabled;
    });
  }

  /**
   * Method for retrieving IsNonEquipmentPurchaseEnabled feature toggle value
   * @private
   */
  private _isNonEquipmentPurchaseEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.NON_EQUIPMENT_PURCHASE).pipe(
      tap((isEnabled: boolean) => {
        if (isEnabled) {
          this.configurationFacade.getNonEquipmentPurchaseSupportedCategories$().pipe(
            take(1),
          ).subscribe((categories: string[]) => {
            this.supportedCategoriesForNonEquipmentPurchase = categories;
          });
        }
        this.isNonEquipmentPurchaseEnabled = isEnabled;
      }),
      take(1),
    ).subscribe();
  }
}
